import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { Logging } from '@newrelic/browser-agent/features/logging';
import config from './config';
import { DEFAULT_TAGS } from './libs/Logging';

/**
 * Extends the global Window interface to include an optional `newrelic` property.
 * This allows TypeScript to recognize `window.newrelic` when integrating with New Relic or similar libraries.
 */
declare global {
  interface Window {
    newrelic?: NewRelicAgent;
  }
}

export interface NewRelicAgentConfig {
  init: {
    distributed_tracing: { enabled: boolean };
    privacy: { cookies_enabled: boolean };
    ajax: { deny_list: string[] };
  }; // NREUM.init
  info: {
    beacon: string;
    errorBeacon: string;
    licenseKey: string;
    applicationID: string;
    sa: number;
  }; // NREUM.info
  loader_config: {
    accountID: string;
    trustKey: string;
    agentID: string;
    licenseKey: string;
    applicationID: string;
  }; // NREUM.loader_config
}

export enum NewRelicLogLevel {
  DEBUG = 'DEBUG',
  ERROR = 'ERROR',
  INFO = 'INFO',
  TRACE = 'TRACE',
  WARN = 'WARN',
}

interface NewRelicAgent {
  log(message: string, options?: { customAttributes?: any; level?: NewRelicLogLevel }): void;
  setUserId(value: string | null): void;
  setCustomAttribute(key: string, value: string | number | boolean): void;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function initializeNewRelicAgent() {
  const {
    newRelicSettings: { agentEnabled, agentConfig },
  } = config;

  if (!agentEnabled) {
    return;
  }

  const options = {
    ...agentConfig,
    features: [Logging],
  };

  new BrowserAgent(options);

  // Set custom tags on browser events (PageView, BrowserInteraction, PageAction).
  // Note: Span events do NOT support custom attributes per NR support (NR-479377).
  Object.entries(DEFAULT_TAGS).forEach(([key, value]) => {
    window.newrelic?.setCustomAttribute(key, value);
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function setNewRelicUser(value: string | null) {
  window.newrelic?.setUserId(value);
}
